import {
  ChatItem,
  MessageSource,
  SourceMetadata,
  ToolCallStatus,
} from "features/assistant/definitions";
import { HistoryMessage } from "services/assistant/websocket";

const mapHistoryStatus = (status?: string): ToolCallStatus => {
  if (status === "completed") return "completed";
  if (status === "running") return "running";
  return "error";
};

const sourceOf = (metadata?: SourceMetadata): MessageSource =>
  metadata?.source ?? "assistant";

/**
 * Converts MESSAGE_HISTORY_SNAPSHOT messages into a flat ChatItem[] array plus the
 * per-message source metadata map (keyed by message id), matching ChatState's shape.
 * Text blocks become message items; tool_use blocks become independent tool_call items.
 * Consecutive text blocks within the same HistoryMessage are merged into one message
 * item, EXCEPT when their source differs — an "answers" block after an "assistant" block
 * starts a new message item so the Answers content stays a distinct, verbatim message.
 */
export const convertHistoryMessages = (
  historyMessages: HistoryMessage[],
): { items: ChatItem[]; messageMeta: Record<string, SourceMetadata> } => {
  const items: ChatItem[] = [];
  const messageMeta: Record<string, SourceMetadata> = {};

  for (const msg of historyMessages) {
    let pendingText: string | null = null;
    let pendingDisplayText: string | undefined;
    let pendingMetadata: SourceMetadata | undefined;

    const flushText = () => {
      if (pendingText !== null) {
        const messageId = crypto.randomUUID();
        items.push({
          type: "message",
          id: messageId,
          messageId,
          role: msg.role,
          content: pendingText,
          displayContent: pendingDisplayText,
          isStreaming: false,
        });
        if (pendingMetadata) {
          messageMeta[messageId] = pendingMetadata;
        }
        pendingText = null;
        pendingDisplayText = undefined;
        pendingMetadata = undefined;
      }
    };

    for (const block of msg.content) {
      if (block.type === "text") {
        const blockSource = sourceOf(block.source_metadata);
        // Merge only the assistant's own consecutive text blocks. Each Answers result is kept
        // as its own message so its citations + conversation/message ids stay intact — matching
        // the live path, where every ask_answers call streams under its own message id.
        const canMerge =
          pendingText !== null &&
          blockSource === "assistant" &&
          sourceOf(pendingMetadata) === "assistant";
        if (canMerge) {
          pendingText += `\n\n${block.text || ""}`;
        } else {
          flushText();
          pendingText = block.text || "";
          pendingDisplayText = block.display_text || undefined;
          pendingMetadata = block.source_metadata;
        }
      } else if (block.type === "tool_use") {
        flushText();
        items.push({
          type: "tool_call",
          id: crypto.randomUUID(),
          toolUseId: crypto.randomUUID(),
          toolName: block.tool_name || "unknown",
          status: mapHistoryStatus(block.status),
          description: block.description,
        });
      } else {
        console.warn("Unknown history content block type:", block.type);
      }
    }

    flushText();
  }

  return { items, messageMeta };
};
